import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Layout Components
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import { LoadingScreen } from './components/LoadingScreens';
import NotificationPermission from './components/NotificationPermission';

// Pages
import Dashboard from './pages/Dashboard';
import ManagementDashboard from './pages/managementDashboard';
import Attendance from './pages/Attendance';
import AttendanceHistory from './pages/AttendanceHistory';
import Calendar from './pages/CalendarPage';
import LeaveRequest from './pages/LeaveRequest';
import LeaveApproval from './pages/LeaveApproval';
import Employees from './pages/Employees';
import Teams from './pages/Teams';
import Reports from './pages/Reports';
import Claims from './pages/Claims';
import WFHRequest from './pages/WFHRequest';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';
import Announcements from './pages/Announcements';
import Profile from './pages/Profile';
import Login from './pages/Login';
import Help from './pages/Help';
import AttendanceCorrection from './pages/AttendanceCorrection';
import ManualMarkAttendance from './pages/ManualMarkAttendance';
import WFHManagement from './pages/WFHManagement';
import LeaveManagement from './pages/LeaveManagement';

// Utils
import ProtectedRoute from './utils/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ToastProvider } from './contexts/ToastContext';
import { PerformanceBarProvider } from './contexts/PerformanceBarContext';
import { SearchProvider, useHeaderSearch } from './contexts/SearchContext';
import { initializePortalAuth } from './utils/portalAuth';

function AppContent() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { clearHeaderSearch } = useHeaderSearch();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLoading, setShowLoading] = useState(true);
  const [loadingStartTime] = useState(Date.now());

  // Clear header search when navigating between pages
  React.useEffect(() => {
    clearHeaderSearch();
  }, [location.pathname]);

  // Initialize portal authentication on app start
  React.useEffect(() => {
    const { switchSuccess, currentPortal } = initializePortalAuth();
    if (switchSuccess) {
      console.log('Portal switch successful, current portal:', currentPortal);
      // Optionally show a success message
    }
  }, []);

  // Ensure loading screen shows for at least 2 seconds
  React.useEffect(() => {
    if (!loading) {
      const elapsedTime = Date.now() - loadingStartTime;
      const remainingTime = Math.max(0, 4000 - elapsedTime);
      
      if (remainingTime > 0) {
        const timer = setTimeout(() => {
          setShowLoading(false);
        }, remainingTime);
        
        return () => clearTimeout(timer);
      } else {
        setShowLoading(false);
      }
    }
  }, [loading, loadingStartTime]);
  
  const isPublicRoute = ['/login', '/help'].includes(location.pathname);
  
  if (loading || showLoading) {
    return <LoadingScreen />;
  }

  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/help" element={<Help />} />
      </Routes>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      {/* Main Content */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <Header onMenuClick={() => setSidebarOpen(true)} />
        
        {/* Page Content — min-h-0 so this region can shrink and scroll inside h-screen flex */}
        <main className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-auto overscroll-y-contain">
          <div className="min-w-0 p-4 lg:p-5">
            <Routes>
              <Route path="/" element={<Navigate to={user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'hr' ? '/management-dashboard' : '/dashboard'} />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/management-dashboard" element={<ProtectedRoute><ManagementDashboard /></ProtectedRoute>} />
              <Route path="/attendance" element={<ProtectedRoute><Attendance /></ProtectedRoute>} />
              <Route path="/attendance-history" element={<ProtectedRoute><AttendanceHistory /></ProtectedRoute>} />
              <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
              <Route path="/leave-request" element={<ProtectedRoute><LeaveRequest /></ProtectedRoute>} />
              <Route path="/wfh-request" element={<ProtectedRoute><WFHRequest /></ProtectedRoute>} />
              <Route path="/leave-approval" element={<ProtectedRoute><LeaveApproval /></ProtectedRoute>} />
              <Route path="/employees" element={<ProtectedRoute><Employees /></ProtectedRoute>} />
              <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
              <Route path="/claims" element={<ProtectedRoute><Claims /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
              <Route path="/announcements" element={<ProtectedRoute><Announcements /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/admin-settings" element={<Navigate to="/settings" replace />} />
              <Route path="/holiday-management" element={<Navigate to="/calendar" replace />} />
              <Route path="/wfh-management" element={<ProtectedRoute><WFHManagement /></ProtectedRoute>} />
              <Route path="/leave-management" element={<ProtectedRoute><LeaveManagement /></ProtectedRoute>} />
              <Route path="/attendance-correction" element={<ProtectedRoute><AttendanceCorrection /></ProtectedRoute>} />
              <Route path="/mark-attendance" element={<ProtectedRoute><ManualMarkAttendance /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            </Routes>
          </div>
        </main>
      </div>
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Notification Permission Banner */}
      <NotificationPermission />
    </div>
  );
}

function App() {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  
  if (!clientId) {
    console.error('REACT_APP_GOOGLE_CLIENT_ID is not defined in environment variables');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-4">Configuration Error</h2>
          <p className="text-gray-600">Google Client ID is not configured. Please check your environment variables.</p>
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <AuthProvider>
        <NotificationProvider>
          <ToastProvider>
            <PerformanceBarProvider>
              <SearchProvider>
                <Router>
                  <AppContent />
                </Router>
              </SearchProvider>
            </PerformanceBarProvider>
          </ToastProvider>
        </NotificationProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
